/**
 * AP·무기로부터 파생 스탯을 계산하는 순수 함수.
 * Phaser 를 import 하지 않는다.
 */

import { BASIC_ATTACK, BASIC_RANGED, PLAYER, STATS, type SkillDef } from '../data/balance';
import { getJob, jobApStart, type BasicAttackStyle } from '../data/jobs';
import { getWeapon, type WeaponDef } from '../data/weapons';

export interface AbilityPoints {
  str: number;
  dex: number;
  int: number;
  luk: number;
  unspent: number;
}

export interface DerivedStats {
  maxHp: number;
  maxMp: number;
  attackMin: number;
  attackMax: number;
  defense: number;
  critRate: number;
  critMultiplier: number;
  accuracy: number;
  avoid: number;
}

export function createStartingAp(jobId?: string): AbilityPoints {
  const start = jobApStart(jobId);
  return { ...start, unspent: STATS.ap.unspent };
}

/** 세이브에 AP 가 없을 때: 직업 시작 배분 + (레벨 - 시작 레벨) × 5 미사용 AP */
export function apForLoadedLevel(level: number, jobId?: string): AbilityPoints {
  const extra = Math.max(0, level - PLAYER.startLevel) * STATS.ap.perLevel;
  return { ...jobApStart(jobId), unspent: extra };
}

export function clampAp(
  raw: Partial<AbilityPoints> | undefined,
  level: number,
  jobId?: string,
): AbilityPoints {
  const fallback = apForLoadedLevel(level, jobId);
  const start = jobApStart(jobId);
  const str = Math.max(start.str, Math.floor(raw?.str ?? fallback.str));
  const dex = Math.max(start.dex, Math.floor(raw?.dex ?? fallback.dex));
  const int = Math.max(start.int, Math.floor(raw?.int ?? fallback.int));
  const luk = Math.max(start.luk, Math.floor(raw?.luk ?? fallback.luk));
  const spentExtra = str - start.str + (dex - start.dex) + (int - start.int) + (luk - start.luk);
  const budget = fallback.unspent;
  const unspent = Math.max(0, Math.floor(raw?.unspent ?? Math.max(0, budget - spentExtra)));
  return { str, dex, int, luk, unspent };
}

export function deriveStats(
  level: number,
  ap: AbilityPoints,
  weapon: WeaponDef = getWeapon(undefined),
  jobId?: string,
  gear?: { watk: number; def: number; acc: number },
): DerivedStats {
  const steps = Math.max(0, level - PLAYER.startLevel);
  const b = STATS.base;
  const p = STATS.perLevel;
  const g = STATS.ap.gain;
  const start = jobApStart(jobId);
  const dStr = ap.str - start.str;
  const dDex = ap.dex - start.dex;
  const dInt = ap.int - start.int;
  const dLuk = ap.luk - start.luk;
  const primary = getJob(jobId).attackStat;
  const dAtk =
    primary === 'dex' ? dDex : primary === 'int' ? dInt : primary === 'luk' ? dLuk : dStr;
  const dOffhand = primary === 'str' ? dDex : 0;
  const bonus = gear ?? { watk: 0, def: 0, acc: 0 };
  return {
    maxHp: b.maxHp + p.maxHp * steps + Math.floor(dStr * g.hpPerStr),
    maxMp: b.maxMp + p.maxMp * steps + Math.floor(dInt * g.mpPerInt),
    attackMin:
      b.attackMin +
      p.attackMin * steps +
      Math.floor(dAtk * g.attackMinPerStr + dOffhand * g.attackMinPerDex) +
      weapon.watkMin +
      bonus.watk,
    attackMax:
      b.attackMax +
      p.attackMax * steps +
      Math.floor(dAtk * g.attackMaxPerStr + dOffhand * g.attackMaxPerDex) +
      weapon.watkMax +
      bonus.watk,
    defense: b.defense + p.defense * steps + Math.floor(dDex * g.defensePerDex) + bonus.def,
    critRate: b.critRate + p.critRate * steps + dLuk * g.critPerLuk,
    critMultiplier: b.critMultiplier,
    accuracy: Math.max(1, Math.floor(ap.dex * g.accPerDex + ap.luk * g.accPerLuk) + weapon.accuracy + bonus.acc),
    avoid: Math.max(1, Math.floor(ap.dex * g.avoidPerDex + ap.luk * g.avoidPerLuk)),
  };
}

/** 직업 Ctrl 기본 공격. 전사는 근접, 나머지는 투사체. */
export function basicAttackForJob(jobId?: string): SkillDef {
  const style: BasicAttackStyle = getJob(jobId).basicAttack ?? 'melee';
  if (style !== 'bolt' && style !== 'arrow' && style !== 'star') {
    return { ...BASIC_ATTACK, kind: 'melee' };
  }
  const ranged = BASIC_RANGED[style];
  return {
    ...BASIC_ATTACK,
    name: ranged.name,
    nameEn: ranged.nameEn,
    description: ranged.description,
    icon: ranged.icon,
    kind: 'projectile',
    effect: ranged.effect,
    projectile: { ...ranged.projectile },
    lungeVelocity: ranged.lungeVelocity,
    knockback: ranged.knockback,
    hits: 1,
    maxTargets: 1,
  };
}

/** 무기 공속·사거리를 스킬 판정에 반영한다. 시전 모션 길이(castMs)는 애니메이션과 맞추려고 그대로 둔다. */
export function applyWeaponToSkill(skill: SkillDef, weapon: WeaponDef): SkillDef {
  if (skill.kind === 'buff' || skill.kind === 'projectile') {
    return {
      ...skill,
      cooldownMs: Math.max(80, Math.round(skill.cooldownMs * weapon.speed)),
    };
  }
  const basic = skill.id === 'attack';
  const range = basic ? weapon.range : 1 + (weapon.range - 1) * 0.35;
  return {
    ...skill,
    cooldownMs: Math.max(80, Math.round(skill.cooldownMs * weapon.speed)),
    hitbox: {
      ...skill.hitbox,
      width: Math.round(skill.hitbox.width * range),
      offsetX: Math.round(skill.hitbox.offsetX * range),
    },
  };
}
